/**
 * postMessage event contract
 * --------------------------------------------------------------------------
 * The Signer's Choice tech check is designed to be embedded inside a parent
 * application via an <iframe>. Because embedders frequently gate their own
 * flows on the outcome of the tech check (for example: only allowing a user
 * to continue once they pass, redirecting to a support screen on failure, or
 * connecting the user to a live chat agent), the tech check emits a stream of
 * events to the parent window using `window.parent.postMessage`.
 *
 * All messages are one-way (iframe -> parent) and share a single versioned
 * envelope so integrators can reliably identify and parse them.
 */

/**
 * Identifies messages that originate from this application. Embedders should
 * always check `event.data.source === TECH_CHECK_MESSAGE_SOURCE` before acting
 * on a message, since a window can receive postMessages from many sources.
 */
export const TECH_CHECK_MESSAGE_SOURCE = 'signerschoice-tech-check' as const;

/**
 * The schema version of the message envelope. Bump this whenever a breaking
 * change is made to the envelope or payload shapes so that embedders can
 * branch on it if needed.
 *
 * v2: adds `networkPath` to the connectivity-passed and complete payloads
 *     (the restrictive-network / VPN heuristic derived from the preflight
 *     report's selected ICE candidate).
 */
export const TECH_CHECK_MESSAGE_VERSION = 2 as const;

/**
 * All event types the tech check can emit.
 */
export enum TechCheckEventType {
  /** Emitted once when the application has mounted and is ready. */
  Ready = 'ready',
  /** Camera & microphone permissions were granted. */
  DeviceCheckPassed = 'device-check-passed',
  /** Camera & microphone permissions were denied / errored. */
  DeviceCheckFailed = 'device-check-failed',
  /** The user's browser is supported. */
  BrowserCheckPassed = 'browser-check-passed',
  /** The user's browser is not supported. */
  BrowserCheckFailed = 'browser-check-failed',
  /** Connectivity (signaling / TURN / preflight) succeeded. */
  ConnectivityPassed = 'connectivity-passed',
  /** Connectivity (signaling / TURN / preflight) failed. */
  ConnectivityFailed = 'connectivity-failed',
  /** The quality & performance score has been computed. */
  QualityResult = 'quality-result',
  /**
   * The entire tech check has completed. Carries the overall pass/fail result
   * plus the full final test-results JSON (identical to the downloadable
   * report).
   */
  Complete = 'complete',
  /** The user chose to restart the test (fired just before a page reload). */
  Restarted = 'restarted',
}

/** Human readable quality score, mirrors the internal `QualityScore` enum. */
export type QualityScoreLabel = 'Poor' | 'Suboptimal' | 'Good' | 'Excellent';

/**
 * How the user's device connected to the media network during the preflight
 * test. See `src/utils/getNetworkPathType.ts`. A relayed path (especially
 * `relayed-tcp`) often indicates a VPN, firewall, or strict NAT.
 */
export type NetworkPath = 'direct' | 'relayed-udp' | 'relayed-tcp' | null;

/** A single measured quality metric. */
export interface QualityMetric {
  average?: string;
  max?: string;
  qualityScore: number;
}

export interface QualityResultPayload {
  /** True when the overall quality score is Good or Excellent. */
  passed: boolean;
  score: QualityScoreLabel;
  metrics: {
    latency: QualityMetric;
    jitter: QualityMetric;
    packetLoss: QualityMetric;
    bitrate: QualityMetric;
  };
}

export interface CompletePayload {
  /** True when all tests passed (quality score of Good or Excellent). */
  passed: boolean;
  score: QualityScoreLabel;
  /**
   * How the user's device connected during the preflight test. Surfaced at the
   * top level (in addition to being present inside `results`) so embedders can
   * react to a relayed / likely-VPN path without parsing the full report.
   */
  networkPath: NetworkPath;
  /**
   * The full final test-results object. This is the same structure produced
   * by the downloadable report and is intentionally typed loosely because it
   * aggregates several third-party report shapes.
   */
  results: Record<string, unknown>;
}

/** Payload shapes keyed by event type. Events without extra data map to `void`. */
export interface TechCheckEventPayloads {
  [TechCheckEventType.Ready]: void;
  [TechCheckEventType.DeviceCheckPassed]: void;
  [TechCheckEventType.DeviceCheckFailed]: { reason?: string };
  [TechCheckEventType.BrowserCheckPassed]: void;
  [TechCheckEventType.BrowserCheckFailed]: void;
  [TechCheckEventType.ConnectivityPassed]: { networkPath: NetworkPath };
  [TechCheckEventType.ConnectivityFailed]: { reason?: string; networkPath: NetworkPath };
  [TechCheckEventType.QualityResult]: QualityResultPayload;
  [TechCheckEventType.Complete]: CompletePayload;
  [TechCheckEventType.Restarted]: void;
}

/**
 * The envelope wrapping every message sent to the parent window.
 */
export interface TechCheckMessage<T extends TechCheckEventType = TechCheckEventType> {
  source: typeof TECH_CHECK_MESSAGE_SOURCE;
  version: typeof TECH_CHECK_MESSAGE_VERSION;
  type: T;
  /** Epoch milliseconds when the message was created. */
  timestamp: number;
  payload: TechCheckEventPayloads[T];
}
